// Keeps the tastings in progress on this device (proef. keys, so "delete my data" clears them with
// the rest, lib/localData.ts) and tells screens when they change. One tasting is in front of the
// taster (the active run, which every tasting screen reads); starting or resuming another parks the
// one it replaces on a shelf instead of dropping it, so every unfinished tasting can be offered
// back. A tasting belongs to the account that made it: it carries the owner's id, and another person
// signing in on this device neither sees nor continues it. What is stored is read back through a
// schema, so a record from an older build or a damaged one is dropped rather than crashing the
// screen that reads it.
import { useSyncExternalStore } from "react";
import { TastingRunSnapshot } from "@proef/contract";
import { newRun, isUnfinished, type TastingRun } from "./run";
import type { NoteDepth } from "../noteDraft";

const KEY = "proef.tastingRun";
const SHELF_KEY = "proef.tastingShelf";
/** Parked tastings kept; the least recently changed one makes room for the next. */
const SHELF_LIMIT = 10;
/** A tasting from before runs were stamped: older than any other copy of it. */
const NEVER = "1970-01-01T00:00:00.000Z";

const StoredRun = TastingRunSnapshot.extend({
  updatedAt: TastingRunSnapshot.shape.updatedAt.default(NEVER),
});

function parseRun(value: unknown): TastingRun | undefined {
  const parsed = StoredRun.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

function readKey(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeKey(key: string, value: unknown): void {
  try {
    if (value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked (private mode): the tasting lives for this page only.
  }
}

function readShelf(): TastingRun[] {
  const stored = readKey(SHELF_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.flatMap((item: unknown) => {
    const run = parseRun(item);
    return run === undefined ? [] : [run];
  });
}

interface State {
  readonly active: TastingRun | undefined;
  readonly shelf: readonly TastingRun[];
}

let state: State =
  typeof window === "undefined"
    ? { active: undefined, shelf: [] }
    : { active: parseRun(readKey(KEY)), shelf: readShelf() };
/** Who is signed in; undefined signed out or not yet known. New changes are stamped with it. */
let owner: string | undefined;
const listeners = new Set<() => void>();

const byRecent = (a: TastingRun, b: TastingRun): number => b.updatedAt.localeCompare(a.updatedAt);

/** Parks `run` when it still asks something of the taster; a finished one has nothing to come back to. */
function park(shelf: readonly TastingRun[], run: TastingRun | undefined): readonly TastingRun[] {
  const rest = shelf.filter((held) => held.id !== run?.id);
  if (run === undefined || !isUnfinished(run)) return rest;
  return [run, ...rest].toSorted(byRecent).slice(0, SHELF_LIMIT);
}

function commit(next: State): void {
  state = next;
  writeKey(KEY, next.active);
  writeKey(SHELF_KEY, next.shelf.length === 0 ? undefined : next.shelf);
  listeners.forEach((listener) => {
    listener();
  });
}

/** A change made here: stamped with the time, and with the owner when it has none yet. */
function stamped(run: TastingRun): TastingRun {
  return {
    ...run,
    updatedAt: new Date().toISOString(),
    ...(run.ownerId === undefined && owner !== undefined ? { ownerId: owner } : {}),
  };
}

const sameRun = (a: TastingRun, b: TastingRun): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Whether `viewer` may see this tasting: their own, or one made while nobody was signed in. */
export function visibleTo(run: TastingRun, viewer: string | undefined): boolean {
  return run.ownerId === undefined || run.ownerId === viewer;
}

export function getRun(): TastingRun | undefined {
  return state.active;
}

/** Every tasting on this device the signed-in person may continue, the active one first. */
export function getRuns(): readonly TastingRun[] {
  return [state.active, ...state.shelf].filter(
    (run): run is TastingRun => run !== undefined && visibleTo(run, owner),
  );
}

/** The whole shelf and the active run, whoever owns them: for the sync, which names the owner itself. */
export function getAllRuns(): readonly TastingRun[] {
  return [state.active, ...state.shelf].filter((run): run is TastingRun => run !== undefined);
}

/** Begins a tasting. Another one still in progress is parked, not lost. */
export function startRun(init: { depth: NoteDepth; blind: boolean; title: string }): TastingRun {
  const run = stamped(newRun(init));
  commit({ active: run, shelf: park(state.shelf, state.active) });
  return run;
}

/** Puts a ready-made run in place (a flight this device just joined). The one it replaces is parked. */
export function setRun(run: TastingRun): void {
  const next = stamped(run);
  commit({
    active: next,
    shelf: park(
      state.shelf.filter((held) => held.id !== next.id),
      state.active?.id === next.id ? undefined : state.active,
    ),
  });
}

/** Changes the tasting in progress; with none, or no actual change, there is nothing to do. */
export function updateRun(change: (run: TastingRun) => TastingRun): void {
  const current = state.active;
  if (current === undefined) return;
  const changed = change(current);
  // A flight's poll hands the same table back over and over: that is not a change to carry anywhere.
  if (sameRun(current, changed)) return;
  commit({ ...state, active: stamped(changed) });
}

/** Ends the tasting in front of the taster. Parked ones stay. */
export function clearRun(): void {
  commit({ ...state, active: undefined });
}

/** Throws a tasting away, wherever it is held. */
export function discardRun(id: string): void {
  commit({
    active: state.active?.id === id ? undefined : state.active,
    shelf: state.shelf.filter((run) => run.id !== id),
  });
}

/** Brings a parked tasting (or the active one, which stays) in front of the taster. */
export function activateRun(id: string): TastingRun | undefined {
  if (state.active?.id === id) return state.active;
  const found = state.shelf.find((run) => run.id === id);
  if (found === undefined) return undefined;
  commit({
    active: found,
    shelf: park(
      state.shelf.filter((run) => run.id !== id),
      state.active,
    ),
  });
  return found;
}

/**
 * Takes another device's copy of a tasting as the one in front of the taster, keeping the stamp it
 * came with so it does not look newer than it is. A copy of a tasting held here replaces it.
 */
export function adoptRun(run: TastingRun, options: { active: boolean }): void {
  const rest = state.shelf.filter((held) => held.id !== run.id);
  const others = state.active?.id === run.id ? undefined : state.active;
  if (options.active || state.active?.id === run.id) {
    commit({ active: run, shelf: park(rest, others) });
  } else {
    commit({ active: state.active, shelf: park(rest, run) });
  }
}

/**
 * Tells the store who is signed in. A tasting in front of the taster that belongs to someone else
 * goes to the shelf, where only its owner finds it again; one made while signed out stays.
 */
export function switchOwner(viewer: string | undefined): void {
  owner = viewer;
  const active = state.active;
  if (active === undefined || visibleTo(active, viewer)) return;
  commit({ active: undefined, shelf: park(state.shelf, active) });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const subscribeRuns = subscribe;
/** Calls `listener` whenever the tasting changes; the presence beacon follows it. */
export const subscribeRun = subscribe;

export function useRun(): TastingRun | undefined {
  return useSyncExternalStore(subscribe, getRun, () => undefined);
}

let visible:
  | {
      readonly source: State;
      readonly owner: string | undefined;
      readonly runs: readonly TastingRun[];
    }
  | undefined;

/** The tastings the signed-in person may continue, with one identity per state so React can compare. */
function snapshotRuns(): readonly TastingRun[] {
  if (visible?.source !== state || visible.owner !== owner) {
    visible = { source: state, owner, runs: getRuns() };
  }
  return visible.runs;
}

const NONE: readonly TastingRun[] = [];

export function useRuns(): readonly TastingRun[] {
  return useSyncExternalStore(subscribe, snapshotRuns, () => NONE);
}
